import { Building, Check, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { WorkerSalon } from "@/gql/graphql";
import type { Salon } from "@/lib/store/salon";
import { SalonInfoCard } from "../cards/salon-info-card";

interface SalonSelectionSectionProps {
  currentSalon: Salon;
  availableSalons: Salon[];
  userSalonRoles: WorkerSalon[];
  onSalonChange: (salonId: string) => void;
}

export function SalonSelectionSection({
  currentSalon,
  availableSalons,
  userSalonRoles,
  onSalonChange,
}: SalonSelectionSectionProps) {
  const navigate = useNavigate();

  const showChevron = true; // Always show chevron like in left sidebar

  return (
    <div>
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
        Current Salon
      </h3>

      <div className="relative">
        {showChevron ? (
          <button
            type="button"
            onClick={() => navigate("/salon-selector")}
            className="flex items-center w-full cursor-pointer hover:bg-accent/50 rounded-lg p-2 -m-2 transition-colors"
          >
            <div className="flex-1">
              <SalonInfoCard salon={currentSalon} userSalonRoles={userSalonRoles} />
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex items-center w-full cursor-pointer hover:bg-accent/50 rounded-lg p-2 -m-2 transition-colors">
                <div className="flex-1">
                  <SalonInfoCard salon={currentSalon} userSalonRoles={userSalonRoles} />
                </div>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="min-w-[300px] max-w-[400px]" align="end">
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                Switch Salon
              </div>
              <DropdownMenuSeparator />
              {availableSalons?.map((salon) => {
                const isSelected = currentSalon.id === salon.id;

                return (
                  <DropdownMenuItem
                    key={salon.id}
                    onClick={() => onSalonChange(salon.id)}
                    className="py-3 px-2 cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center min-w-0 flex-1">
                        <Building className="w-4 h-4 mr-3 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{salon.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {salon.city ? `${salon.city}, ${salon.state}` : salon.address}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center ml-2 flex-shrink-0">
                        {isSelected && <Check className="w-4 h-4 text-primary" />}
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!showChevron && (
          <div className="flex items-center">
            <div className="flex-1">
              <SalonInfoCard salon={currentSalon} userSalonRoles={userSalonRoles} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
